import React from 'react';
import { Typography, Button, Space } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;

export interface StatusModalButton {
  key: string;
  type?: 'primary' | 'default' | 'dashed' | 'text' | 'link';
  danger?: boolean;
  icon?: React.ReactNode;
  children: React.ReactNode;
  onClick: () => void | Promise<void>;
  loading?: boolean;
  size?: 'small' | 'middle' | 'large';
}

interface StatusModalProps {
  type: 'loading' | 'error' | 'warning' | 'info' | 'success';
  icon?: React.ReactNode;
  title?: React.ReactNode;
  description?: React.ReactNode;
  buttons?: StatusModalButton[];
  padding?: string;
}

const StatusModal: React.FC<StatusModalProps> = ({
  type,
  icon,
  title,
  description,
  buttons = [],
  padding = '20px',
}) => {
  const getDefaultIcon = () => {
    if (icon) return icon;
    
    switch (type) {
      case 'loading':
        return <LoadingOutlined style={{ fontSize: '48px', color: '#1890ff' }} />;
      case 'error':
        return <div style={{ fontSize: '48px', color: '#ff4d4f' }}>❌</div>;
      case 'warning':
        return <div style={{ fontSize: '48px', color: '#faad14' }}>⚠️</div>;
      case 'success':
        return <div style={{ fontSize: '48px', color: '#52c41a' }}>✅</div>;
      case 'info':
      default:
        return <div style={{ fontSize: '48px', color: '#1890ff' }}>ℹ️</div>;
    }
  };

  const getDefaultTitle = () => {
    if (title) return title;
    
    switch (type) {
      case 'loading':
        return '載入中...';
      case 'error':
        return '發生錯誤';
      case 'warning':
        return '警告';
      case 'success':
        return '完成';
      case 'info':
      default:
        return '系統通知';
    }
  };

  return (
    <div style={{ textAlign: 'center', padding }}>
      <div style={{ marginBottom: '16px' }}>
        {getDefaultIcon()}
      </div>
      
      {(title || type) && (
        <Title level={4} style={{ marginBottom: '16px' }}>
          {getDefaultTitle()}
        </Title>
      )}
      
      {description && (
        <div style={{ marginBottom: buttons.length > 0 ? '24px' : '0' }}>
          {typeof description === 'string' ? <Text>{description}</Text> : description}
        </div>
      )}
      
      {buttons.length > 0 && (
        <Space size="middle">
          {buttons.map((button) => (
            <Button
              key={button.key}
              type={button.type}
              danger={button.danger}
              icon={button.icon}
              onClick={button.onClick}
              loading={button.loading}
              size={button.size || 'large'}
            >
              {button.children}
            </Button>
          ))}
        </Space>
      )}
    </div>
  );
};

export default StatusModal;
